import { calculateTaxAmount, calculateDepreciation, calculateShortTermGewSt, applySection15aLimit } from './taxCalculations';
import { calculateKaufnebenkosten } from '../data/acquisitionCostConfig';
import { gewerbesteuerData } from '../data/taxEntityConfig';
import { calculateLandlordCO2Cost } from '../data/co2Config';
import { vatRates, kleinunternehmerThreshold } from '../data/vatConfig';

export const calculateFinancials = (costs, revenueData) => {
  const horizonYears = costs.projection?.horizonYears || 15;
  const years = Array.from({ length: horizonYears }, (_, i) => i + 1);
  const results = {};

  // === INITIAL CALCULATIONS ===
  const purchasePrice = costs.property.purchasePrice;
  const landSharePercent = costs.property.landSharePercent;
  const landValue = purchasePrice * (landSharePercent / 100);
  const buildingValue = purchasePrice - landValue;
  const buildingShareFraction = buildingValue / purchasePrice;

  // Kaufnebenkosten
  let startupCostsTotal;
  if (costs.startup.grunderwerbsteuer > 0 || costs.startup.notarkosten > 0 ||
      costs.startup.grundbuchkosten > 0 || costs.startup.maklerkosten > 0) {
    startupCostsTotal = costs.startup.grunderwerbsteuer +
      costs.startup.notarkosten +
      costs.startup.grundbuchkosten +
      costs.startup.maklerkosten;
  } else {
    const nebenkosten = calculateKaufnebenkosten(
      costs.property.bundesland,
      purchasePrice,
      costs.purchase.debtCapital
    );
    startupCostsTotal = nebenkosten.total;
  }

  // Renovation budget
  const renovationBudget = costs.startup?.renovationBudget || 0;
  const renovationCapitalized = costs.depreciation?.renovationTreatment === 'capitalize'
    ? renovationBudget : 0;

  // Depreciable basis: building portion of price + building portion of acquisition costs + capitalized renovation
  const depreciableBasis = buildingValue +
    (startupCostsTotal * buildingShareFraction) +
    renovationCapitalized;

  // Total initial outflow
  const totalAcquisitionCost = purchasePrice + startupCostsTotal + renovationBudget;
  const totalCapitalRaised = costs.purchase.equityCapital + costs.purchase.debtCapital;
  const initialCash = totalCapitalRaised - totalAcquisitionCost;

  // === LOAN SETUP ===
  let loanBalance = costs.purchase.debtCapital;
  const baseInterestRate = costs.loan.interestRate / 100;
  const zinsbindungYears = costs.loan.zinsbindungYears || 10;
  const tilgungPercent = costs.loan.tilgungPercent / 100;
  const tilgungsfreieJahre = costs.loan.tilgungsfreieJahre || 0;
  const refinancingSpread = (costs.loan.refinancingRateSpread || 0) / 100;

  // Initial annuity: Tilgung + Zins on initial balance
  const initialAnnuity = loanBalance * (tilgungPercent + baseInterestRate);

  // === TAX SETUP ===
  const entityType = costs.taxEntity?.type || 'private';
  const personalTaxRate = (costs.taxEntity?.personalTaxRate || 42) / 100;
  const municipality = costs.taxEntity?.municipality || 'berlin';
  const hebesatz = gewerbesteuerData.cities[municipality]?.hebesatz || 410;

  // === ENTITY VALIDATION ===
  let effectiveEntityType = entityType;
  const warnings = [];
  if (entityType === 'immo_gmbh' && costs.revenue?.rentalType === 'shortterm') {
    warnings.push('immo_gmbh_shortterm_conflict');
    effectiveEntityType = 'gmbh'; // Loses erweiterte Kürzung
  }

  // === AfA SETUP ===
  const yearBuilt = costs.property.yearBuilt || 1985;
  const afaMethod = costs.depreciation?.afaMethod || 'standard';

  let cumulativeCash = initialCash;
  let cumulativeReserves = 0;
  let previousBuildingBookValue = depreciableBasis;
  const buildingOnlyDepreciableBasis = depreciableBasis - renovationCapitalized;
  let previousBuildingOnlyBookValue = buildingOnlyDepreciableBasis;
  let currentLoanBalance = loanBalance;
  let lossCarryForward = 0;
  let section15aCapitalAccount = costs.taxEntity?.kommanditeinlage || 25000;

  // Renovation expense (if expensed in year 1)
  const renovationExpenseYear1 = costs.depreciation?.renovationTreatment === 'expense'
    ? renovationBudget : 0;

  const inflationRate = (costs.projection?.inflationRate || 2) / 100;

  years.forEach((year) => {
    const yearData = {};
    const inflationFactor = Math.pow(1 + inflationRate, year - 1);

    // === REVENUE ===
    yearData.revenue = revenueData?.[year]?.netRent || revenueData?.[year]?.revenue || 0;

    // === COSTS ===
    // Variable costs: maintenance (inflated)
    const maintenanceLower = (costs.variableRecurring?.maintenancePercent?.lower || 1.0) / 100;
    const maintenanceUpper = (costs.variableRecurring?.maintenancePercent?.upper || 1.5) / 100;
    const maintenanceRate = (maintenanceLower + maintenanceUpper) / 2;
    const maintenanceCost = buildingValue * maintenanceRate * inflationFactor;

    // Fixed costs (inflated)
    const grundsteuer = (costs.fixedRecurring?.grundsteuer || 0) * inflationFactor;
    const insurance = (costs.fixedRecurring?.gebaeudeversicherung || 0) * inflationFactor;

    // Hausgeld non-recoverable (only for apartments, inflated)
    let hausgeldNonRecoverable = 0;
    if (costs.property.propertyType === 'eigentumswohnung' && costs.fixedRecurring?.hausgeld > 0) {
      hausgeldNonRecoverable = costs.fixedRecurring.hausgeld * 12 *
        (costs.fixedRecurring.hausgeldNichtUmlagefaehigPercent || 30) / 100 * inflationFactor;
    }

    // Property management (% of revenue — auto-inflates with revenue, no extra factor)
    const propertyManagementPercent = (costs.fixedRecurring?.propertyManagementPercent || 0) / 100;
    const propertyManagementCost = yearData.revenue * propertyManagementPercent;

    // Custom fixed costs (inflated)
    const customFixedCosts = (costs.fixedRecurring?.custom || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0) * inflationFactor;

    const fixedCosts = grundsteuer + insurance + hausgeldNonRecoverable +
      propertyManagementCost + customFixedCosts;

    // Renovation expense (year 1 only)
    const refitExpense = (year === 1) ? renovationExpenseYear1 : 0;

    // Capital expenditures
    const scheduledCapex = (costs.scheduledCapex?.[year] || [])
      .reduce((sum, item) => sum + (item.cost || 0), 0);

    // Reserve contribution: % of building value
    const reserveContribution = (buildingValue * (costs.emergencyFund?.annualReservePercent || 2)) / 100;

    // === DEPRECIATION (AfA) ===
    // Separate renovation depreciation from building AfA
    const renovationDepYears = costs.depreciation?.renovationDepreciationYears || 10;
    const renovationAnnualDep = (renovationCapitalized > 0 && year <= renovationDepYears)
      ? renovationCapitalized / renovationDepYears : 0;

    // Building AfA: on building-only basis (excl. renovation)
    const buildingAfA = calculateDepreciation(
      buildingOnlyDepreciableBasis,
      yearBuilt,
      afaMethod,
      year,
      afaMethod === 'degressive' ? previousBuildingOnlyBookValue : null
    );
    previousBuildingOnlyBookValue = Math.max(0, previousBuildingOnlyBookValue - buildingAfA);

    const annualDepreciation = buildingAfA + renovationAnnualDep;
    const currentBuildingBookValue = Math.max(0, previousBuildingBookValue - annualDepreciation);

    // === LOAN ===
    let currentInterestRate = baseInterestRate;
    let interestExpense = 0;
    let principalPayment = 0;
    let annualLoanPayment = 0;

    if (currentLoanBalance > 0) {
      // Anschlussfinanzierung: new rate after Zinsbindung expires
      if (year > zinsbindungYears) {
        currentInterestRate = baseInterestRate + refinancingSpread;
      }

      interestExpense = currentLoanBalance * currentInterestRate;

      if (year <= tilgungsfreieJahre) {
        // Interest-only period
        principalPayment = 0;
        annualLoanPayment = interestExpense;
      } else {
        // Annuity payment: constant annual payment (Tilgung + Zins)
        // After Zinsbindung, recalculate annuity for remaining balance
        if (year === zinsbindungYears + 1 && refinancingSpread !== 0) {
          // Recalculate annuity at new rate for remaining term
          const remainingYears = horizonYears - year + 1;
          if (remainingYears > 0 && currentInterestRate > 0) {
            annualLoanPayment = currentLoanBalance *
              (currentInterestRate * Math.pow(1 + currentInterestRate, remainingYears)) /
              (Math.pow(1 + currentInterestRate, remainingYears) - 1);
          } else {
            annualLoanPayment = currentLoanBalance / Math.max(1, remainingYears);
          }
        } else if (year <= zinsbindungYears || refinancingSpread === 0) {
          annualLoanPayment = initialAnnuity;
        }
        // For years after refinancing year 1, keep the recalculated annuity
        // Store in results for carry-forward
        else if (year > zinsbindungYears + 1 && refinancingSpread !== 0 && results[year - 1]) {
          annualLoanPayment = results[year - 1].details.annualLoanPayment;
        }

        principalPayment = Math.min(annualLoanPayment - interestExpense, currentLoanBalance);
        if (principalPayment < 0) principalPayment = 0;
      }
    }

    currentLoanBalance = Math.max(0, currentLoanBalance - principalPayment);

    // === ADDITIONAL WERBUNGSKOSTEN ===
    // Disagio: Year 1 only, immediately deductible if marktüblich
    const disagioAmount = year === 1
      ? costs.purchase.debtCapital * (costs.werbungskosten?.disagioPercent || 0) / 100
      : 0;

    // Recurring Werbungskosten (inflated)
    const steuerberater = (costs.werbungskosten?.steuerberaterAnnual || 0) * inflationFactor;
    const sonstigeWK = (costs.werbungskosten?.sonstigePauschale || 0) * inflationFactor;
    const mietausfallVers = yearData.revenue * (costs.werbungskosten?.mietausfallversicherungPercent || 0) / 100;
    const additionalWerbungskosten = disagioAmount + steuerberater + sonstigeWK + mietausfallVers;

    // === CO2 COST ALLOCATION (landlord share) ===
    const currentCalendarYear = new Date().getFullYear() + year - 1;
    const co2LandlordCost = calculateLandlordCO2Cost(
      costs.property?.energieausweis || 'D',
      costs.property?.heatingType || 'gas',
      costs.property?.squareMeters || 75,
      currentCalendarYear
    );

    // === VAT FOR SHORT-TERM RENTAL ===
    let vatCollected = 0;
    let vatInputDeduction = 0;
    let netVAT = 0;
    const isShortTerm = costs.revenue?.rentalType === 'shortterm';

    if (isShortTerm) {
      const vatMode = costs.vat?.vatLiable || 'auto';
      const isVatLiable = vatMode === 'yes' ||
        (vatMode === 'auto' && yearData.revenue > kleinunternehmerThreshold);

      if (isVatLiable) {
        // Revenue includes 7% USt — extract collected VAT
        vatCollected = yearData.revenue * vatRates.accommodation / (1 + vatRates.accommodation);
        // Year 1 Vorsteuerabzug on renovation costs (19% input VAT)
        if (year === 1 && renovationBudget > 0) {
          vatInputDeduction = renovationBudget * vatRates.inputVAT / (1 + vatRates.inputVAT);
        }
        netVAT = vatCollected - vatInputDeduction;
      }
    }

    // === P&L ===
    yearData.grossProfit = yearData.revenue - maintenanceCost;
    yearData.operatingExpenses = fixedCosts + refitExpense + additionalWerbungskosten + co2LandlordCost;
    yearData.ebitda = yearData.grossProfit - yearData.operatingExpenses;
    yearData.depreciation = annualDepreciation;
    yearData.ebit = yearData.ebitda - annualDepreciation;
    yearData.ebt = yearData.ebit - interestExpense;

    // Tax: entity-specific with Verlustvortrag (loss carry-forward)
    let taxableIncome = yearData.ebt;
    yearData.lossOffset = 0;

    if (effectiveEntityType === 'gmbh_co_kg') {
      // §15a: losses limited by capital account
      const result = applySection15aLimit(
        yearData.ebt,
        costs.taxEntity?.kommanditeinlage || 25000,
        section15aCapitalAccount
      );
      section15aCapitalAccount = result.capitalAccount;

      if (yearData.ebt < 0) {
        // Loss: only deductible portion reduces tax
        taxableIncome = 0;
        yearData.section15aCarriedLoss = result.carriedLoss;
      } else {
        // Profit: normal treatment, offset prior Verlustvortrag
        if (taxableIncome > 0 && lossCarryForward > 0) {
          const offset = Math.min(taxableIncome, lossCarryForward);
          taxableIncome -= offset;
          lossCarryForward -= offset;
          yearData.lossOffset = offset;
        }
      }
    } else {
      // Standard loss carry-forward for other entities
      if (taxableIncome > 0 && lossCarryForward > 0) {
        const offset = Math.min(taxableIncome, lossCarryForward);
        taxableIncome -= offset;
        lossCarryForward -= offset;
        yearData.lossOffset = offset;
      } else if (taxableIncome < 0) {
        lossCarryForward += Math.abs(taxableIncome);
        taxableIncome = 0;
      }
    }
    const kirchensteuerRate = costs.taxEntity.kirchensteuerEnabled
      ? (costs.taxEntity.kirchensteuerRate || 0.09) : 0;
    const otherIncome = costs.taxEntity.otherAnnualIncome || 0;
    yearData.taxExpense = taxableIncome > 0
      ? calculateTaxAmount(effectiveEntityType, taxableIncome, personalTaxRate, hebesatz, kirchensteuerRate, otherIncome)
      : 0;

    // GewSt for short-term rental with hotel services (private entity only)
    yearData.shortTermGewSt = 0;
    yearData.shortTermGewStAnrechnung = 0;
    if (isShortTerm && costs.vat?.hotelServices &&
        effectiveEntityType === 'private' && taxableIncome > 0) {
      const gewStResult = calculateShortTermGewSt(taxableIncome, hebesatz);
      yearData.shortTermGewSt = gewStResult.gewst;
      yearData.shortTermGewStAnrechnung = gewStResult.estAnrechnung;
      // Net additional tax: GewSt minus ESt credit
      yearData.taxExpense += gewStResult.gewst - Math.min(gewStResult.estAnrechnung, yearData.taxExpense);
    }

    yearData.lossCarryForward = lossCarryForward;
    yearData.netIncome = yearData.ebt - yearData.taxExpense;

    // === CASH FLOW ===
    // VAT is balance-sheet neutral for income tax, but affects cash flow
    yearData.cashFromOperations = yearData.netIncome + annualDepreciation - reserveContribution - netVAT;
    yearData.cashFromInvesting = -scheduledCapex;
    yearData.cashFromFinancing = -principalPayment;
    yearData.freeCashFlow = yearData.cashFromOperations + yearData.cashFromInvesting + yearData.cashFromFinancing;

    cumulativeCash += yearData.freeCashFlow;
    cumulativeReserves += reserveContribution;

    // === MARKET VALUE (split appreciation: land vs building) ===
    const landAppreciationRate = costs.projection?.landAppreciationRate !== undefined
      ? costs.projection.landAppreciationRate / 100
      : (costs.projection?.appreciationRate || 2) / 100;
    const buildingAppreciationRate = costs.projection?.buildingAppreciationRate !== undefined
      ? costs.projection.buildingAppreciationRate / 100
      : (costs.projection?.appreciationRate || 2) / 100;
    const landMarketValue = landValue * Math.pow(1 + landAppreciationRate, year);
    const buildingMarketValue = buildingValue * Math.pow(1 + buildingAppreciationRate, year);
    yearData.marketValue = landMarketValue + buildingMarketValue;
    yearData.landMarketValue = landMarketValue;
    yearData.buildingMarketValue = buildingMarketValue;

    // === BALANCE SHEET ===
    // Land value includes land portion of acquisition costs (not depreciable, stays constant)
    const landWithAcquisitionCosts = landValue + (startupCostsTotal * (1 - buildingShareFraction));
    const propertyBookValue = landWithAcquisitionCosts + currentBuildingBookValue;

    yearData.assets = {
      landValue: landWithAcquisitionCosts,
      buildingBookValue: currentBuildingBookValue,
      propertyBookValue,
      cash: cumulativeCash,
      reserves: cumulativeReserves,
      totalAssets: propertyBookValue + cumulativeCash + cumulativeReserves,
    };

    yearData.liabilities = {
      loanBalance: currentLoanBalance,
      totalLiabilities: currentLoanBalance,
    };

    yearData.equity = {
      initialInvestment: costs.purchase.equityCapital,
      retainedEarnings: results[year - 1]
        ? results[year - 1].equity.retainedEarnings + yearData.netIncome
        : yearData.netIncome,
      totalEquity: 0,
    };
    yearData.equity.totalEquity = yearData.equity.initialInvestment + yearData.equity.retainedEarnings;

    // === MEMO LINES (supplementary, not part of accounting balance sheet) ===
    yearData.memo = {
      landMarketValue,
      buildingMarketValue,
      totalMarketValue: yearData.marketValue,
      unrealizedGain: yearData.marketValue - propertyBookValue,
      ltv: yearData.marketValue > 0 ? currentLoanBalance / yearData.marketValue : 0,
      nav: yearData.marketValue + cumulativeCash + cumulativeReserves - currentLoanBalance,
    };

    // === DETAIL LINE ITEMS ===
    yearData.details = {
      revenue: yearData.revenue,
      maintenanceCost,
      fixedCosts,
      grundsteuer,
      insurance,
      hausgeldNonRecoverable,
      propertyManagementCost,
      refitExpense,
      reserveContribution,
      scheduledCapex,
      depreciation: annualDepreciation,
      interestExpense,
      principalPayment,
      annualLoanPayment,
      currentInterestRate,
      bookValue: propertyBookValue,
      totalAcquisitionCost,
      // Additional Werbungskosten
      disagio: disagioAmount,
      steuerberater,
      sonstigeWK,
      mietausfallVers,
      additionalWerbungskosten,
      // CO2 cost
      co2LandlordCost,
      // VAT
      vatCollected,
      vatInputDeduction,
      netVAT,
    };

    results[year] = yearData;
    previousBuildingBookValue = currentBuildingBookValue;
  });

  results.warnings = warnings;
  return results;
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercent = (decimal, decimals = 1) => {
  return (decimal * 100).toFixed(decimals) + '%';
};
